import React, { FC, useCallback, useContext, useMemo } from "react";
import { IUsePaginationStore } from "utils/usePaginationStore";
import { LocalizeText } from "components/localizer";
import VKButton from "components/vkButton";
import { useNavigate } from "react-router-dom";
import { Box, IconButton } from "@mui/material";
import { THeadCell } from "utils/types";
import TableComponent, { IRow } from "components/tableComponent";
import { getActivatedInSpaceflow, getDeactivatedInSpaceflow, getMemberStatusText } from "utils/helpers";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { EditMember } from "components/drawers/members/editMember";
import { ModalContext } from "contexts/modals";
import { isAfter, isPast, parseISO } from "date-fns";
import { IMemberSubscriptionAssociation } from "reducers/members";
import { DeleteOutline } from "@mui/icons-material";
import { MembersContext } from "contexts/members";
import { usePermission } from "utils/hooks/usePermission";

interface IMembersByMembershipTableProps {
    pagination: IUsePaginationStore<"member-subscription-associations">;
    refreshTable: () => Promise<void>;
}

export const MembersByMembershipTable: FC<IMembersByMembershipTableProps> = ({ pagination, refreshTable }) => {
    const theme = useTheme();
    const largeScreen = useMediaQuery(theme.breakpoints.up("xl"));
    const navigate = useNavigate();
    const { hasPermissions } = usePermission();
    const { openDrawer } = React.useContext(ModalContext);
    const { removeAssociation } = useContext(MembersContext);

    const membersHeaderCells: THeadCell[] = [
        {
            id: "id",
            sortId: "subscription__id",
            label: <LocalizeText tag="id" />,
        },
        { id: "e-mail", sortId: "member__email", label: <LocalizeText tag="email" /> },
        {
            id: "name",
            sortId: "member__first_name",
            label: <LocalizeText tag="name" />,
        },
        { id: "invitedToSpaceflow", label: <LocalizeText tag="invitedToSpaceflow" /> },
        { id: "status", label: <LocalizeText tag="status" /> },
        { id: "terminatedInSpaceflow", label: <LocalizeText tag="terminatedInSpaceflow" /> },
        { id: "action", label: <></> },

    ];

    const showEditButton = useCallback(
        (association: IMemberSubscriptionAssociation) => {
            return (
                hasPermissions(["change_membersubscriptionassociation"]) &&
                !isPast(parseISO(association.deactivates ?? ""))
            );
        },
        [hasPermissions]
    );

    const onClear = useCallback(
        async (association: IMemberSubscriptionAssociation): Promise<void> => {
            await removeAssociation(association.uuid);
            pagination.query.refetch();
        },
        [pagination.query, removeAssociation]
    );


    const showClearButton = useCallback((association: IMemberSubscriptionAssociation) => {
        return isAfter(parseISO(association.activates ?? ""), parseISO(association.deactivates ?? ""));
    }, []);

    const getButton = useCallback(
        (association: IMemberSubscriptionAssociation) => {
            if (showClearButton(association)) {
                return (
                    <IconButton
                        key={`association_row_${association.uuid}`}
                        onClick={() => onClear(association)}
                        sx={{
                            marginLeft: "17px",
                            color: "black",
                            ":hover": { backgroundColor: "rgba(0, 0, 0, 0.1)" },
                        }}
                    >
                        <DeleteOutline />
                    </IconButton>
                );
            }
            if (showEditButton(association)) {
                return (
                    <VKButton
                        key={`association_row_${association.uuid}`}
                        template="primary"
                        tag="edit"
                        size="small"
                        onClick={() =>
                            openDrawer(
                                EditMember,
                                {
                                    association,
                                    pagination: pagination,
                                    updateSubscription: refreshTable,
                                    onConfirmRemove: () => {
                                        pagination.query.refetch();
                                    },
                                },
                                "editMember"
                            )
                        }
                    />
                );
            }
            return null;
        },
        [onClear, openDrawer, pagination, showClearButton, showEditButton]
    );


    const rows = useMemo((): IRow[] => {
        return pagination.results.map((association) => ({
            disabled:
                isPast(parseISO(association.deactivates ?? "")) ||
                isAfter(parseISO(association.activates ?? ""), parseISO(association.deactivates ?? "")),
            data: [
                association.subscription_id,
                association.member.email,
                association.member.first_name ? `${association.member.first_name} ${association.member.last_name}` : "-",
                getActivatedInSpaceflow(association.member),
                isAfter(parseISO(association.activates ?? ""), new Date())
                    ? ""
                    : getMemberStatusText(association.member, true),
                getDeactivatedInSpaceflow(association.member),
                getButton(association)
            ],
            tooltipText: isAfter(parseISO(association.activates ?? ""), parseISO(association.deactivates ?? "")) ? (
                <LocalizeText tag="invalidDatesInfo" />
            ) : (
                ""
            ),
        }));
    }, [pagination.results, largeScreen, navigate]);

    return (
        <Box>
            <TableComponent
                headCells={membersHeaderCells}
                skeletonVariant="options"
                pagination={pagination}
                rows={rows}
            />
        </Box>
    );
};
